/* eslint-disable no-console */
import { Instance } from '@funtech-int/rival';

import { BaseLogger, Logger, LogLevels, Transport, TransportsOptions } from '@kinopoisk-int/logger';

import { loggerRef } from '.';

function createConsoleTransport(): Transport {
    return {
        trace: console.trace.bind(console),
        debug: console.debug.bind(console),
        info: console.info.bind(console),
        warn: console.warn.bind(console),
        error: console.error.bind(console),
        fatal: console.error.bind(console),
    };
}

const asIsContextValueFormatter = (value: unknown) => value;

const contextValuesFormatters: Record<string, Function> = {
    error: asIsContextValueFormatter,
    userId: asIsContextValueFormatter,
    invalidFeatureParams: asIsContextValueFormatter,
    featureParamsSourceLayers: asIsContextValueFormatter,
    moduleName: asIsContextValueFormatter,
    logLevel: asIsContextValueFormatter,
    operationName: asIsContextValueFormatter,
    requestTime: asIsContextValueFormatter,
    stack: asIsContextValueFormatter,
    status: asIsContextValueFormatter,
    errorName: asIsContextValueFormatter,
    errorMessage: asIsContextValueFormatter,
    errorPath: asIsContextValueFormatter,
    errorType: asIsContextValueFormatter,
    errorClassification: asIsContextValueFormatter,
    errorReason: asIsContextValueFormatter,
    contentUuid: asIsContextValueFormatter,
    crossRedirect: asIsContextValueFormatter,
};

function formatContext(context: object) {
    return Object.fromEntries(
        Object.entries(context)
            .filter(([key]) => key in contextValuesFormatters)
            .map(([key, value]) => [key, contextValuesFormatters[key]?.(value)])
    );
}

function createRumTransport(): Transport {
    function logRumEvent(
        logLevel: 'trace' | 'debug' | 'info' | 'warn',
        context: object,
        message: string = ''
    ) {
        const additional = formatContext({ logLevel, ...context });
        window.Ya.Rum?.logEventString(message, '', { additional });
    }

    function logRumError(context: object, message?: string) {
        const additional = formatContext(context);
        window.Ya.Rum?.logError({ message, additional });
    }

    return {
        trace(context, message) {
            logRumEvent('trace', context, message);
        },

        debug(context, message) {
            logRumEvent('debug', context, message);
        },

        info(context, message) {
            logRumEvent('info', context, message);
        },

        warn(context, message) {
            logRumEvent('warn', context, message);
        },

        error(context, message) {
            logRumError(context, message);
        },

        fatal(context: object, message?: string): void {
            logRumError(context, message);
        },
    };
}

interface LoggerParams {
    withConsoleTransport: boolean;
}

export function createLogger({ withConsoleTransport }: LoggerParams): Instance<Logger> {
    const transportsOptions: TransportsOptions[] = [
        {
            logLevel: LogLevels.Trace,
            transport: createRumTransport(),
        },
    ];

    if (withConsoleTransport) {
        transportsOptions.push({
            logLevel: LogLevels.Trace,
            transport: createConsoleTransport(),
        });
    }

    const logger = new BaseLogger({
        transportsOptions,
    });

    return {
        trace: logger.trace.bind(logger),
        debug: logger.debug.bind(logger),
        info: logger.info.bind(logger),
        warn: logger.warn.bind(logger),
        error: logger.error.bind(logger),
        fatal: logger.fatal.bind(logger),
        child: logger.child.bind(logger),
        ref() {
            return loggerRef;
        },
    };
}
